import axios from 'axios';

/**
 * Resolve the backend base URL.
 *
 * Why: `REACT_APP_BACKEND_URL` is baked into the bundle at build time. If a
 * build produced on the Emergent preview is later served on a custom
 * production domain (e.g. www.clicksyhub.com), all API calls would keep
 * pointing at the Emergent preview host.
 *
 * Strategy:
 *   1. In the browser, inspect window.location.origin.
 *   2. If we are running on a real production domain (not an Emergent host
 *      and not localhost), try the SAME origin first — assumes the production
 *      deployment ingress proxies /api to the backend.
 *   3. A response interceptor watches for network errors and 5xx responses
 *      on that same-origin base. On the very first such failure we flip the
 *      baseURL to the baked-in REACT_APP_BACKEND_URL and transparently retry
 *      the failed request. This keeps the site working while the operator
 *      fixes the production ingress.
 *   4. Otherwise (localhost dev, emergent preview, SSR) use REACT_APP_BACKEND_URL.
 */
const ENV_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function pickInitialOrigin() {
  if (typeof window === 'undefined' || !window.location) return ENV_URL;
  const origin = window.location.origin || '';
  const host = (window.location.hostname || '').toLowerCase();
  const isEmergent = /(^|\.)emergent(agent\.com|\.host)$/.test(host);
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
  if (origin && !isEmergent && !isLocal) return origin;
  return ENV_URL;
}

let BACKEND_URL = pickInitialOrigin();
export const API = `${BACKEND_URL}/api`;

let fallbackActivated = false;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ch_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config || {};
    // If the request is failing against our same-origin production base and
    // we have an ENV_URL different from it, flip the baseURL and retry once.
    const networkLike = !err?.response || (err.response.status >= 500 && err.response.status <= 599);
    if (
      networkLike &&
      !fallbackActivated &&
      !cfg.__chFallbackTried &&
      ENV_URL &&
      api.defaults.baseURL &&
      !api.defaults.baseURL.startsWith(ENV_URL)
    ) {
      fallbackActivated = true;
      BACKEND_URL = ENV_URL;
      api.defaults.baseURL = `${ENV_URL}/api`;
      try {
        // eslint-disable-next-line no-console
        console.warn('[ClicksyHub] production /api unavailable on', window.location.origin, '— falling back to', ENV_URL);
      } catch (_) {}
      const retryCfg = { ...cfg, __chFallbackTried: true, baseURL: api.defaults.baseURL };
      return api.request(retryCfg);
    }

    if (err?.response?.status === 401) {
      localStorage.removeItem('ch_token');
      localStorage.removeItem('ch_user');
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin') && !window.location.pathname.endsWith('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;
